import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";

export enum PartTimelineSource {
  Part,
  Offer,
  OrderHistory,
}

export enum PartOrderStatus {
  Sold,
  Picked,
  Shipped,
  Cancelled,
  Returned,
}

export interface IPartTimelineChange {
  field: string;
  old: string | null;
  new: string | null;
}

export interface IPartTimelineEvent {
  id: string;
  timestamp: string;
  source: PartTimelineSource;
  entityName: string;
  entityId: string;
  isActiveOffer: boolean;
  action: string;
  userId: string | null;
  milestones: string[];
  isKeyEvent: boolean;
  changes: IPartTimelineChange[];
  orderNumber: string | null;
  orderStatus: PartOrderStatus | null;
  soldDate: string | null;
  returnedOn: string | null;
}

export interface IPartTimelineOffer {
  id: string;
  sku: string | null;
  ebayId: string | null;
  isActive: boolean;
  publishedOn: string | null;
  listingStarted: string | null;
  listingEnded: string | null;
  created: string;
}

export interface IPartTimelineEbayIdentity {
  ebayItemNumber: string | null;
  ebayTradingApiItemId: string | null;
  ebayInventoryApiOfferId: string | null;
  activeOfferEbayId: string | null;
  itemIdMismatch: boolean;
}

export interface IPartTimeline {
  partDataId: string;
  tagNumber: string | null;
  activePartOfferId: string;
  saleCount: number;
  returnCount: number;
  soldMoreThanOnce: boolean;
  ebayIdentity: IPartTimelineEbayIdentity;
  offers: IPartTimelineOffer[];
  events: IPartTimelineEvent[];
}

interface IPartTimelineResponse {
  success: boolean;
  message: string;
  data: IPartTimeline;
}

export default function usePartTimeline(partId: string | undefined, componentName = "usePartTimeline") {
  const { get } = useApiHelper(apiConfig(componentName));

  const partTimeline = useQuery({
    queryKey: ["partTimeline", partId],
    queryFn: () => get<IPartTimelineResponse>(`/part/${partId}/timeline`),
    enabled: !!partId,
    refetchOnWindowFocus: false,
  });

  return { partTimeline };
}
